import { formatLocalDateTime } from '../../utils/localDateTime';
import React, { useEffect, useState } from 'react';
import { collection, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import type { UserProfile } from '@types';
import { db } from '../../../firebaseConfig';
import SupportChatModal, { type SupportChatRecord } from './SupportChatModal';

const displayDate = (value: unknown) => formatLocalDateTime(value, 'Pending');
const displaySummary = (chat: SupportChatRecord) => !chat.summary || chat.summary === 'Summary is being prepared.' ? chat.lastMessagePreview : chat.summary;

const GlobalChatsPanel: React.FC<{ currentUser: UserProfile }> = ({ currentUser }) => {
    const [chats, setChats] = useState<SupportChatRecord[]>([]);
    const [selected, setSelected] = useState<SupportChatRecord | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        setLoading(true);
        setError('');
        const constraints = [];
        if (currentUser.adminRole === 'Regional')
            constraints.push(where('managerUid', '==', currentUser.uid));
        const chatsQuery = query(collection(db, 'support_chats'), ...constraints, orderBy('lastMessageAt', 'desc'));
        return onSnapshot(chatsQuery, snapshot => {
            const next = snapshot.docs.map(document => ({ id: document.id, ...document.data() } as SupportChatRecord));
            setChats(next);
            setSelected(previous => previous ? next.find(chat => chat.id === previous.id) || previous : null);
            setLoading(false);
        }, snapshotError => {
            setError(snapshotError.message);
            setLoading(false);
        });
    }, [currentUser.adminRole, currentUser.uid]);

    return (
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm animate-fade-in">
            <div className="flex items-center justify-between gap-4 border-b border-slate-200 p-5">
                <div><h2 className="text-xl font-extrabold text-slate-900">Chats</h2><p className="mt-1 text-sm text-slate-500">Client conversations across your permitted organizations.</p></div>
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600">{chats.length} total</span>
            </div>
            {error && <p className="m-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
            {loading ? (
                <div className="flex items-center justify-center py-20 text-slate-400"><svg className="mr-3 h-7 w-7 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>Loading chats…</div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="px-5 py-3">Summary</th><th className="px-5 py-3">Organization</th><th className="px-5 py-3">Client User</th><th className="px-5 py-3">Manager</th><th className="px-5 py-3">Started</th><th className="px-5 py-3">Last Message</th></tr></thead>
                        <tbody className="divide-y divide-slate-100">
                            {chats.map(chat => (
                                <tr key={chat.id} onClick={() => setSelected(chat)} className="cursor-pointer hover:bg-blue-50/50">
                                    <td className="max-w-sm px-5 py-4"><p className="font-semibold text-slate-800">{displaySummary(chat)}</p>{chat.unreadForManager && <span className="mt-1 inline-flex rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold uppercase text-blue-700">New message</span>}</td>
                                    <td className="px-5 py-4"><p className="font-semibold text-slate-800">{chat.organizationName}</p><p className="text-xs text-slate-400">{chat.region}</p></td>
                                    <td className="px-5 py-4"><p className="font-semibold text-slate-800">{chat.participantName}</p><p className="text-xs text-slate-400">{chat.participantEmail}</p></td>
                                    <td className="px-5 py-4 text-slate-600">{chat.managerName}</td>
                                    <td className="whitespace-nowrap px-5 py-4 text-xs text-slate-500">{displayDate(chat.startedAt)}</td>
                                    <td className="whitespace-nowrap px-5 py-4 text-xs text-slate-500">{displayDate(chat.lastMessageAt)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {!chats.length && !error && <p className="p-10 text-center text-sm text-slate-400">No chats found.</p>}
                </div>
            )}
            {selected && <SupportChatModal mode="admin" chat={selected} onClose={() => setSelected(null)} />}
        </section>
    );
};

export default GlobalChatsPanel;
